import React, { useContext, useMemo, useState } from 'react';
import { DataTable } from '@performant-software/semantic-components';
import { FlexRow } from '../layouts';
import { ResourceData, ResourcesContext } from '../providers/ResourcesProvider';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { abandonResource, quickDeleteResource } from '../providers/api';
import { Button, Modal } from 'semantic-ui-react';
import { AuthenticationContext } from '../providers/AuthenticationProvider';
import { TeamMembersContext } from '../providers/TeamMembersProvider';

interface Props {
  deletableResources?: string[]; // list of guids
  linkableLocalIds?: boolean;
  resources: ResourceData[];
  projectId?: number;
  token?: string;
}

interface SortConfig {
  field: string;
  order: 'asc' | 'desc';
}

interface ConfirmationConfig {
  resourceGuid: string;
  type: 'abandon' | 'delete';
}

const RESOURCES_PER_PAGE = 10;

const ResourceTable: React.FC<Props> = (props) => {
  const [confirmation, setConfirmation] = useState<
    ConfirmationConfig | undefined
  >();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortConfig>({
    field: 'localId',
    order: 'desc',
  });

  const { canDeleteResources, canCheckoutResources, systemAdmin, email } = useContext(AuthenticationContext);
  const { isTeamAdmin } = useContext(TeamMembersContext);
  const { fetchResources } = useContext(ResourcesContext);
  const { teamId } = useContext(TeamMembersContext);

  const { t } = useTranslation();

  useMemo(() => {
    props.resources.sort((a, b) => {
      const field = sort.field as keyof ResourceData;
      const aValue = (a[field] || 'zzzzzzz').toString().toLocaleLowerCase();
      const bValue = (b[field] || 'zzzzzzz').toString().toLocaleLowerCase();

      if (aValue > bValue) {
        return sort.order === 'desc' ? 1 : -1;
      } else if (aValue < bValue) {
        return sort.order === 'desc' ? -1 : 1;
      }

      return 0;
    });
  }, [props.resources, sort]);

  const handleColumnClick = (column: { name: string; sortable: boolean }) => {
    if (column.sortable) {
      if (sort.field === column.name) {
        setSort({
          field: sort.field,
          order: sort.order === 'asc' ? 'desc' : 'asc',
        });
      } else {
        setSort({
          field: column.name,
          order: 'desc',
        });
      }
    }
  };

  const renderStatus = (key: string, item: any) => {
    return (
      <FlexRow>
        {item[key] ? (
          <FlexRow
            style={{
              color:
                item[key] === 'checked_out' || item[key] === 'deleted'
                  ? 'red'
                  : 'green',
            }}
          >
            {t(`resources.${item[key]}`)}
          </FlexRow>
        ) : (
          ''
        )}
      </FlexRow>
    );
  };

  const abandon = async () => {
    if (props.token && confirmation && props.projectId) {
      await abandonResource(
        props.projectId,
        confirmation.resourceGuid,
        props.token
      );
    }
  };

  const quickDelete = async () => {
    if (props.token && confirmation && props.projectId) {
      await quickDeleteResource(
        props.projectId,
        confirmation.resourceGuid,
        props.token
      );
    }
  };

  const canAbandon = (resource: ResourceData) => {
    if (resource.status === 'checked_out') {
      if (isTeamAdmin || systemAdmin) {
        return true
      } else if (resource.byEmail === email) {
        return true
      }
    }

    return false;
  }

  const actions = [
    {
      name: 'delete',
      popup: {
        content: t('resources.deletionExplanation'),
        title: t('resources.delete'),
      },
      render: (resource: ResourceData) => {
        if (
          props.deletableResources &&
          teamId &&
          canDeleteResources(teamId) &&
          canCheckoutResources(teamId) &&
          props.deletableResources.includes(resource.guid)
        ) {
          return (
            <Button
              basic
              compact
              icon="trash"
              onClick={() =>
                setConfirmation({
                  resourceGuid: resource.guid,
                  type: 'delete',
                })
              }
            />
          );
        }
      },
    },
    {
      name: 'abandonCheckout',
      icon: 'cut',
      popup: {
        content: t('resources.abandonmentExplanation'),
        title: t('resources.abandon'),
      },
      render: (resource: ResourceData) => {
        if (canAbandon(resource)) {
          return (
            <Button
              basic
              compact
              icon="cut"
              onClick={() =>
                setConfirmation({
                  resourceGuid: resource.guid,
                  type: 'abandon',
                })
              }
            />
          );
        }
      },
    },
  ];

  const columns = [
    {
      name: 'localId',
      label: t('resources.localId'),
      sortable: true,
      render: (item: ResourceData) => {
        if (props.linkableLocalIds) {
          return <Link to={`${item.localId}`}>{item.localId}</Link>;
        } else {
          return <p>{item.localId}</p>;
        }
      },
    },
    {
      name: 'name',
      label: t('resources.name'),
      sortable: true,
    },
    {
      name: 'status',
      label: t('resources.status'),
      sortable: true,
      render: (item: any) => renderStatus('status', item),
    },
    {
      name: 'byUser',
      label: t('resources.byUser'),
      sortable: true,
    },
    {
      name: 'onDate',
      label: t('resources.onDate'),
      sortable: true,
    },
  ];

  return (
    <>
      <DataTable
        actions={actions}
        columns={columns}
        items={props.resources.slice(
          (page - 1) * RESOURCES_PER_PAGE,
          page * RESOURCES_PER_PAGE
        )}
        tableProps={{
          celled: true,
          sortable: true,
          width: '100%',
        }}
        onColumnClick={handleColumnClick}
        onPageChange={(_event: any, data: any) => setPage(data.activePage)}
        page={page}
        pages={Math.floor(props.resources.length / RESOURCES_PER_PAGE) + 1}
        showRecordCount
      />
      {confirmation ? (
        <Modal onClose={() => setConfirmation(undefined)} open>
          <Modal.Header>
            {t(`resources.${confirmation.type}Resource`)}
          </Modal.Header>
          <Modal.Content>
            {confirmation.type === 'abandon'
              ? t('resources.areYouSureYouWantToAbandon')
              : t('resources.doYouWishToDeleteResource')}
          </Modal.Content>
          <Modal.Actions>
            <Button
              content={t('general.cancel')}
              color="grey"
              onClick={() => setConfirmation(undefined)}
            />
            <Button
              content={t('general.confirm')}
              color="red"
              onClick={async () => {
                confirmation.type === 'abandon'
                  ? await abandon()
                  : await quickDelete();

                fetchResources();
                setConfirmation(undefined);
              }}
            />
          </Modal.Actions>
        </Modal>
      ) : null}
    </>
  );
};

export default ResourceTable;
